import React, { useEffect, useMemo, useState } from "react";
import * as categoryService from "../../services/categoryService";

const styles = {
  page: {
    padding: "28px 36px",
    background: "#fbf7fa",
    minHeight: "100%",
  },
  heroTitle: {
    fontSize: "24px",
    fontWeight: 800,
    color: "#8f1632",
    margin: 0,
    letterSpacing: "0.3px",
    textTransform: "uppercase",
  },
  heroSubtitle: {
    color: "#ff4f9a",
    marginTop: 8,
    marginBottom: 28,
    fontWeight: 600,
  },
  card: {
    background: "#fff",
    border: "1px solid #f1d5df",
    borderRadius: 18,
    padding: 22,
    boxShadow: "0 8px 20px rgba(143, 22, 50, 0.04)",
  },
  topBar: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "center",
    flexWrap: "wrap",
    marginBottom: 16,
  },
  title: {
    fontSize: 22,
    fontWeight: 800,
    margin: 0,
    color: "#111827",
  },
  desc: {
    color: "#4b5563",
    marginTop: 6,
    lineHeight: 1.6,
  },
  actions: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    marginBottom: 18,
  },
  input: {
    minWidth: 220,
    flex: 1,
    border: "1px solid #ead5dd",
    borderRadius: 12,
    padding: "11px 14px",
    background: "#fff",
  },
  btn: {
    border: "1px solid #f3c7d7",
    background: "#fff",
    color: "#8f1632",
    borderRadius: 12,
    padding: "10px 14px",
    fontWeight: 700,
    cursor: "pointer",
  },
  btnPrimary: {
    border: "1px solid #8f1632",
    background: "#8f1632",
    color: "#fff",
    borderRadius: 12,
    padding: "10px 14px",
    fontWeight: 700,
    cursor: "pointer",
  },
  tableWrap: {
    overflowX: "auto",
    border: "1px solid #f2e3e8",
    borderRadius: 16,
  },
  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
    minWidth: 760,
  },
  th: {
    textAlign: "left",
    fontSize: 13,
    color: "#6b7280",
    padding: "14px 16px",
    borderBottom: "1px solid #f2e3e8",
    background: "#fff9fb",
    fontWeight: 800,
  },
  td: {
    padding: "14px 16px",
    borderBottom: "1px solid #f7edf1",
    verticalAlign: "top",
    color: "#111827",
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: 999,
    background: "#ecfdf3",
    color: "#027a48",
    fontWeight: 700,
    fontSize: 12,
  },
  actionRow: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
  },
  empty: {
    padding: 18,
    border: "1px dashed #f1c2d3",
    borderRadius: 14,
    color: "#6b7280",
    background: "#fff",
  },
};

function asArray(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function countProduct(item) {
  if (typeof item?.product_count === "number") return item.product_count;
  if (typeof item?.products_count === "number") return item.products_count;
  if (typeof item?.total_products === "number") return item.total_products;
  return 0;
}

function makeSlug(text) {
  return String(text || "")
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

export default function CategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        setLoading(true);
        const payload = await categoryService.getAdminCategories({
          page: 1,
          page_size: 100,
        });
        if (!active) return;
        setCategories(asArray(payload));
      } catch (error) {
        if (!active) return;
        setCategories([]);
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => {
      active = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    if (!keyword) return categories;

    return categories.filter((item) => {
      const hay = [
        item?.name,
        item?.slug,
        item?.description,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return hay.includes(keyword);
    });
  }, [categories, search]);

  async function handleAddCategory() {
    const name = window.prompt("Nama kategori baru:");
    if (!name) return;

    const payload = {
      name,
      slug: makeSlug(name),
      is_active: true,
      sort_order: categories.length + 1,
      description: "",
    };

    if (typeof categoryService.createCategory !== "function") {
      alert("Fitur tambah kategori belum tersedia di service.");
      return;
    }

    try {
      const created = await categoryService.createCategory(payload);
      const item = created?.data || created;
      if (item?.id) {
        setCategories((prev) => [item, ...prev]);
      }
    } catch (error) {
      alert(error?.message || "Gagal menambah kategori.");
    }
  }

  async function handleEdit(item) {
    const nextName = window.prompt("Ubah nama kategori:", item?.name || "");
    if (!nextName) return;

    const payload = {
      ...item,
      name: nextName,
      slug: makeSlug(nextName),
    };

    if (typeof categoryService.updateCategory !== "function") {
      alert("Fitur edit kategori belum tersedia di service.");
      return;
    }

    try {
      const updated = await categoryService.updateCategory(item.id, payload);
      const result = updated?.data || updated;
      setCategories((prev) =>
        prev.map((row) => (row.id === item.id ? { ...row, ...result } : row))
      );
    } catch (error) {
      alert(error?.message || "Gagal mengubah kategori.");
    }
  }

  async function handleDelete(item) {
    const ok = window.confirm(`Hapus kategori "${item?.name}"?`);
    if (!ok) return;

    if (typeof categoryService.deleteCategory !== "function") {
      alert("Fitur hapus kategori belum tersedia di service.");
      return;
    }

    try {
      await categoryService.deleteCategory(item.id);
      setCategories((prev) => prev.filter((row) => row.id !== item.id));
    } catch (error) {
      alert(error?.message || "Gagal menghapus kategori.");
    }
  }

  return (
    <div style={styles.page}>
      <h1 style={styles.heroTitle}>Dashboard Solusi IoT Cerdas</h1>
      <div style={styles.heroSubtitle}>
        Kelola data dan pengaturan untuk website Solusi IoT Cerdas.
      </div>

      <div style={styles.card}>
        <div style={styles.topBar}>
          <div>
            <h2 style={styles.title}>Kelola Kategori</h2>
            <div style={styles.desc}>
              Data kategori sekarang tersambung ke backend admin catalog.
            </div>
          </div>
          <button type="button" style={styles.btnPrimary} onClick={handleAddCategory}>
            + Tambah Kategori
          </button>
        </div>

        <div style={styles.actions}>
          <input
            style={styles.input}
            placeholder="Cari kategori..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {loading ? <div style={styles.empty}>Memuat kategori...</div> : null}

        {!loading && !filtered.length ? (
          <div style={styles.empty}>Belum ada kategori yang cocok.</div>
        ) : null}

        {!loading && filtered.length ? (
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Nama</th>
                  <th style={styles.th}>Slug</th>
                  <th style={styles.th}>Jumlah Produk</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item) => (
                  <tr key={item.id}>
                    <td style={styles.td}>
                      <div style={{ fontWeight: 800 }}>{item?.name || "-"}</div>
                      {item?.description ? (
                        <div style={{ color: "#6b7280", marginTop: 6, fontSize: 13 }}>
                          {item.description}
                        </div>
                      ) : null}
                    </td>
                    <td style={styles.td}>{item?.slug || "-"}</td>
                    <td style={styles.td}>{countProduct(item)}</td>
                    <td style={styles.td}>
                      <span style={styles.badge}>
                        {item?.is_active === false ? "Nonaktif" : "Aktif"}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.actionRow}>
                        <button type="button" style={styles.btn} onClick={() => handleEdit(item)}>
                          Edit
                        </button>
                        <button type="button" style={styles.btn} onClick={() => handleDelete(item)}>
                          Hapus
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </div>
  );
}